import { useEffect, useRef, useState } from "react";
import { Alert, MenuItem, TextField } from "@mui/material";
import { useSnapshot } from "valtio";
import viewerState from "stores/viewer";
import uiState from "stores/ui";
import { isPetModality } from "lib/cornerstone/helpers/niftiDiscovery";
import { selectPetViewerImage } from "lib/cornerstone/petViewer";

export default function PetControls() {
  const {
    niftiPetImages: images,
    niftiActiveModality: modality,
    niftiBases: bases,
    instanceMetadata,
  } = useSnapshot(viewerState);

  const { loading } = useSnapshot(uiState);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const generation = useRef(0);

  useEffect(() => {
    const currentGeneration = ++generation.current;
    setBusy(false);
    setError(null);
    return () => {
      generation.current = currentGeneration + 1;
    };
  }, [instanceMetadata?.StudyInstanceUID]);

  if (!images.length) return null;

  const activeUrl = modality ? bases[modality] : "";
  const options = [
    ...Object.entries(bases)
      .filter(([key]) => !isPetModality(key))
      .map(([key, url]) => ({ url, modality: key, label: key.toUpperCase() })),
    ...images,
  ];

  const disabled = loading || busy;

  async function run(action: () => Promise<void>) {
    const currentGeneration = generation.current;
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (e) {
      if (currentGeneration === generation.current) {
        setError(e instanceof Error ? e.message : "Could not load the image.");
      }
    } finally {
      if (currentGeneration === generation.current) setBusy(false);
    }
  }

  return (
    <section className="mb-4" aria-label="PET Images">
      <div className="bg-stone-700 p-3 rounded">
        <TextField
          select
          fullWidth
          size="small"
          label="Base Image (PET/SUV)"
          value={options.some((o) => o.url === activeUrl) ? activeUrl : ""}
          disabled={disabled}
          onChange={(event) => {
            const image = options.find((o) => o.url === event.target.value);
            if (image)
              run(() => selectPetViewerImage(image.url, image.modality));
          }}
        >
          {options.map((image) => (
            <MenuItem key={image.url} value={image.url}>
              {image.label}
            </MenuItem>
          ))}
        </TextField>

        {busy && (
          <p role="status" className="mt-2 text-sm text-stone-300">
            Loading image…
          </p>
        )}

        {error && (
          <Alert severity="error" className="mt-2">
            {error}
          </Alert>
        )}
      </div>
    </section>
  );
}
